import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RadioChecked from "../../../assets/radio-button-checked.svg";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  RadioGroup,
  Radio,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { brand, error, gray } from "../../../theme/themePrimitives";
import { RoleLevel } from "../../../services/api/types/users/users";
import { LanguageInfo, useLanguages } from "../../../context/LanguagesContext";
import {
  Language,
  Portal,
} from "../../../services/api/types/portalConfiguration/portal";
import { useTranslation } from "../../../context/TranslationContext";
import { usePortal } from "../../../context/PortalsContext";
import { MaxRoleAccessLevelSelect } from "./MaxRoleAccessLevelSelect";

const ADD_NEW_PORTAL_MODAL_WIDTH_PX = 480;

interface AddNewPortalModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  nameError: string;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  subPortals: any[];
  setSubPortals: React.Dispatch<React.SetStateAction<any>>;
  handleAddSubPortal: () => void;
  subDomain: string;
  subDomainError: string;
  handleSubDomainChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedParentId: string | null;
  setSelectedParentId: React.Dispatch<React.SetStateAction<string | null>>;
  authoringTool: boolean;
  customTranslation: boolean;
  handleSwitchChange: (e: any) => void;
  handleCustomTranslationChange: (e: any) => void;
  languagesArray: Language[];
  languagesError: string;
  handleLanguagesChange: (_: any, selected: LanguageInfo[]) => void;
  addNewPortal: () => void;
  buttonLoading: boolean;
  isSub: boolean;
  setIsSub: React.Dispatch<React.SetStateAction<boolean>>;
  isParent: boolean;
  setIsParent: React.Dispatch<React.SetStateAction<boolean>>;
  portalTypeError: string;
  setPortalTypeError: React.Dispatch<React.SetStateAction<string>>;
  setLanguages: React.Dispatch<React.SetStateAction<Language[]>>;
  setAuthoringTool: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomTranslation: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  portalsArray: Portal[];
  spoofingEmail: string;
  spoofingEmailError: string;
  handleSpoofingEmailChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  setSpoofingEmail: React.Dispatch<React.SetStateAction<string>>;
  maxRoleLevel: RoleLevel | "";
  setMaxRoleLevel: React.Dispatch<React.SetStateAction<RoleLevel | "">>;
  maxRoleLevelError: string;
  setMaxRoleLevelError: React.Dispatch<React.SetStateAction<string>>;
  userLevel?: RoleLevel;
}

export const AddNewPortalModal: React.FC<AddNewPortalModalProps> = ({
  openModal,
  languagesArray,
  name,
  nameError,
  handleNameChange,
  subDomain,
  subDomainError,
  handleSubDomainChange,
  languagesError,
  handleLanguagesChange,
  addNewPortal,
  authoringTool,
  customTranslation,
  handleAddSubPortal,
  handleSwitchChange,
  subPortals,
  setSubPortals,
  selectedParentId,
  setSelectedParentId,
  buttonLoading,
  isParent,
  isSub,
  setIsParent,
  setIsSub,
  setAuthoringTool,
  setCustomTranslation,
  setLanguages,
  portalTypeError,
  setPortalTypeError,
  handleCustomTranslationChange,
  handleClose,
  portalsArray,
  spoofingEmail,
  spoofingEmailError,
  handleSpoofingEmailChange,
  setSpoofingEmail,
  maxRoleLevel,
  setMaxRoleLevel,
  maxRoleLevelError,
  setMaxRoleLevelError,
  userLevel,
}) => {
  const { languages } = useLanguages();
  const { translations } = useTranslation();
  const addPortalTranslations =
    translations.translations["portal-configuration"].addPortalModal;
  const [updLangArray, setUpdLangArray] = useState(languagesArray);
  const [currentPortalId, setCurrentPortalId] = useState<string>("");
  const [currentPortalIsMain, setCurrentPortalIsMain] = useState(Boolean);
  const { portals } = usePortal();

  const selectedParentPortal = portalsArray.find(
    (p) => p.portalUid === selectedParentId,
  );
  const maxRoleLockedFromParent =
    isSub &&
    Boolean(selectedParentId) &&
    selectedParentPortal?.maxRoleLevel != null;

  useEffect(() => {
    setUpdLangArray(languagesArray);
  }, [languagesArray]);

  useEffect(() => {
    if (currentPortalId) {
      const filterArr = portalsArray?.filter(
        (list) => list?.portalUid === currentPortalId,
      );
      setUpdLangArray(filterArr[0]?.portalLanguages);
    }
  }, [currentPortalId, portalsArray]);

  return (
    <Modal open={openModal} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
          width: `${ADD_NEW_PORTAL_MODAL_WIDTH_PX}px`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "1rem",
            borderBottom: `1px solid ${gray[300]}`,
          }}
        >
          <Typography variant="h5">
            {addPortalTranslations?.addNewPortalText}
          </Typography>
          <IconButton
            sx={{
              border: "none",
            }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>
        <Stack spacing={2} sx={{ marginTop: "1rem" }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {addPortalTranslations?.name}
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="name"
              placeholder="Enter portal name"
              type="text"
              value={name}
              onChange={handleNameChange}
              variant="outlined"
              size="small"
              error={Boolean(nameError)}
              helperText={nameError}
              slotProps={{
                input: {
                  endAdornment: nameError && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {addPortalTranslations?.subDomain}
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <TextField
                fullWidth
                id="subDomain"
                placeholder="Enter full URL for sub domain"
                type="text"
                value={subDomain}
                onChange={handleSubDomainChange}
                variant="outlined"
                size="small"
                error={Boolean(subDomainError)}
                helperText={subDomainError}
                slotProps={{
                  input: {
                    endAdornment: subDomainError && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                }}
              />
            </Stack>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {addPortalTranslations?.typeOfPortal}
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                sx={{ width: "100%" }}
              >
                <FormControlLabel
                  value={addPortalTranslations?.parentPortal}
                  control={
                    <Radio
                      onChange={(e) => {
                        setPortalTypeError("");
                        setIsParent(e.target.checked);
                        setLanguages([{ languageCode: "en" }]);
                        if (e.target.checked) setIsSub(false);
                      }}
                      checked={isParent}
                      icon={
                        <CircleOutlinedIcon
                          sx={{
                            color: "lightgray",
                          }}
                        />
                      }
                      checkedIcon={<img src={RadioChecked} alt="" />}
                    />
                  }
                  label={addPortalTranslations?.parentPortal}
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  value={addPortalTranslations?.subPortal}
                  control={
                    <Radio
                      onChange={(e) => {
                        setPortalTypeError("");
                        setIsSub(e.target.checked);
                        if (e.target.checked) setIsParent(false);
                      }}
                      checked={isSub}
                      icon={
                        <CircleOutlinedIcon
                          sx={{
                            color: "lightgray",
                          }}
                        />
                      }
                      checkedIcon={<img src={RadioChecked} alt="" />}
                    />
                  }
                  label={addPortalTranslations?.subPortal}
                  sx={{ flex: 1 }}
                />
              </RadioGroup>
            </Box>
            {portalTypeError && (
              <Box>
                <Typography fontSize={"12px"} color={error[600]}>
                  {portalTypeError}
                </Typography>
              </Box>
            )}
          </Stack>
          {isSub && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                This sub portal belongs to what parent portal?
              </Typography>
              <Select
                size="medium"
                value={selectedParentId}
                onChange={(e) => {
                  setSelectedParentId(e.target.value);
                  setCurrentPortalId(e.target.value || "");

                  const selectedPortal = portalsArray.find(
                    (p) => p.portalUid === e.target.value,
                  );

                  if (selectedPortal) {
                    setAuthoringTool(selectedPortal.authoringTool);
                    setCustomTranslation(selectedPortal.customTranslation);
                    setSpoofingEmail(selectedPortal.spoofingEmail || "");
                    setLanguages(selectedPortal.portalLanguages);
                    setUpdLangArray(selectedPortal.portalLanguages);
                    setCurrentPortalIsMain(selectedPortal.isMain);
                    if (selectedPortal.maxRoleLevel != null) {
                      setMaxRoleLevel(selectedPortal.maxRoleLevel as RoleLevel);
                      setMaxRoleLevelError("");
                    } else {
                      setMaxRoleLevel("");
                    }
                  } else {
                    setMaxRoleLevel("");
                    setMaxRoleLevelError("");
                  }
                }}
                sx={{
                  ...(currentPortalIsMain && {
                    backgroundColor: `var(--brand-600)`,
                    "&:hover": {
                      backgroundColor: `var(--brand-600)`,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: `var(--brand-300)`,
                    },
                    "&.Mui-disabled .MuiTypography-body1": {
                      WebkitTextFillColor: "white",
                    },
                    "& .MuiSelect-icon": {
                      color: "white",
                    },
                  }),
                }}
              >
                {portals.portals
                  .filter((p) => !p.isSubPortal && !p.isMain)
                  .map((p) => (
                    <MenuItem
                      key={p.portalUid}
                      value={p.portalUid}
                      sx={{
                        ...(p.isMain && {
                          "&.Mui-selected, &.Mui-focusVisible": {
                            backgroundColor: "#F9F5FF !important",
                            "&:hover": {
                              backgroundColor: "#F9F5FF !important",
                            },
                          },
                          "&:hover": {
                            backgroundColor: "#F9F5FF !important",
                          },
                          "&.MuiMenuItem-root .MuiTypography-body1": {
                            color: "inherit",
                          },
                        }),
                        "&.Mui-selected, &:hover, &.Mui-focusVisible, &.Mui-selected:hover":
                          {
                            backgroundColor: "#F9FAFB",
                          },
                      }}
                    >
                      <Typography
                        sx={{
                          ...(p.isMain && {
                            color: "white",
                          }),
                        }}
                      >
                        {p.name}
                      </Typography>
                    </MenuItem>
                  ))}
              </Select>
            </Stack>
          )}
          <MaxRoleAccessLevelSelect
            value={maxRoleLevel}
            onChange={setMaxRoleLevel}
            onClearError={() => setMaxRoleLevelError("")}
            disabled={maxRoleLockedFromParent}
            error={maxRoleLevelError}
            userLevel={userLevel}
          />
          {isParent && (
            <Stack spacing={1}>
              {subPortals.map((sp, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: gray[100],
                    p: 2,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle2" mb={1}>
                      {`${addPortalTranslations?.subDomain} ${index + 1}`}
                    </Typography>
                    <IconButton
                      sx={{
                        border: "none",
                      }}
                      onClick={() => {
                        const updatedSubPortals = subPortals.filter(
                          (_, i) => i !== index,
                        );
                        setSubPortals(updatedSubPortals);
                      }}
                    >
                      <CloseIcon sx={{ color: gray[700] }} />
                    </IconButton>
                  </Box>
                  <Stack spacing={1} mb={1}>
                    <Typography>
                      {addPortalTranslations?.name}
                      <Box component="span" sx={{ color: `var(--error-700)` }}>
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder={"Enter portal name"}
                      type="text"
                      value={sp.name}
                      onChange={(e) => {
                        const newSubPortals = [...subPortals];
                        newSubPortals[index].name = e.target.value;
                        if (newSubPortals[index].nameError.trim() !== "") {
                          newSubPortals[index].nameError = "";
                        }
                        setSubPortals(newSubPortals);
                      }}
                      variant="outlined"
                      size="small"
                      error={Boolean(sp.nameError)}
                      helperText={sp.nameError}
                      slotProps={{
                        input: {
                          endAdornment: sp.nameError && (
                            <ErrorOutlineIcon color="error" fontSize="small" />
                          ),
                        },
                      }}
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>
                      {addPortalTranslations?.subDomain}
                      <Box component="span" sx={{ color: `var(--error-700)` }}>
                        *
                      </Box>
                    </Typography>
                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                      <TextField
                        fullWidth
                        placeholder="Enter full URL for sub domain"
                        type="text"
                        value={sp.subDomain}
                        onChange={(e) => {
                          const newSubPortals = [...subPortals];
                          newSubPortals[index].subDomain = e.target.value;
                          if (
                            newSubPortals[index].subDomainError.trim() !== ""
                          ) {
                            newSubPortals[index].subDomainError = "";
                          }
                          setSubPortals(newSubPortals);
                        }}
                        variant="outlined"
                        size="small"
                        error={Boolean(sp.subDomainError)}
                        helperText={sp.subDomainError}
                        slotProps={{
                          input: {
                            endAdornment: sp.subDomainError && (
                              <ErrorOutlineIcon
                                color="error"
                                fontSize="small"
                              />
                            ),
                          },
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddSubPortal}
              >
                Add new sub portal
              </Button>
            </Stack>
          )}

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={600} fontSize={16}>
              {addPortalTranslations?.additionalFeaturesServices}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>{addPortalTranslations?.authoringTool}</Typography>
              <Switch
                disabled={isSub && !!selectedParentId}
                checked={authoringTool}
                onChange={handleSwitchChange}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>
                {addPortalTranslations?.customTranslation}
              </Typography>
              <Switch
                disabled={isSub && !!selectedParentId}
                checked={customTranslation}
                onChange={handleCustomTranslationChange}
              />
            </Box>
          </Stack>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2">
                {addPortalTranslations?.spoofingEmail || "Spoofing email"}
              </Typography>
              <Typography variant="caption" sx={{ color: gray[600] }}>
                (Optional)
              </Typography>
            </Box>
            <TextField
              fullWidth
              id="spoofingEmail"
              placeholder="Enter email address"
              type="email"
              value={spoofingEmail}
              onChange={handleSpoofingEmailChange}
              variant="outlined"
              size="small"
              disabled={isSub && !!selectedParentId}
              error={Boolean(spoofingEmailError)}
              helperText={spoofingEmailError}
              slotProps={{
                input: {
                  endAdornment: (
                    <Tooltip
                      title="Email address used for sending notifications on behalf of the portal."
                      arrow
                      placement="top"
                    >
                      <HelpOutlineIcon
                        sx={{
                          fontSize: "16px",
                          color: gray[500],
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ),
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {addPortalTranslations?.language}
            </Typography>
            <Box>
              <Autocomplete
                disabled={isSub && !!selectedParentId}
                multiple
                options={languages.languages.filter(
                  (lang) => lang.langugageCode !== "en",
                )}
                value={
                  updLangArray
                    .map((lang) =>
                      languages.languages.find(
                        (l) => l.langugageCode === lang.languageCode,
                      ),
                    )
                    .filter(Boolean) as LanguageInfo[]
                }
                onChange={handleLanguagesChange}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.langugageCode === value.langugageCode
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.langugageCode}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <img
                      src={option.flag}
                      alt={option.name}
                      width={24}
                      height={24}
                      style={{
                        border: `1px solid ${gray[300]}`,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    {...params}
                    error={Boolean(languagesError)}
                    helperText={languagesError}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                renderValue={(value, getTagProps) =>
                  value.map((option: LanguageInfo, index) => {
                    const tagProps = getTagProps({ index });
                    const { key, ...rest } = tagProps;
                    const isEnglish = option.langugageCode === "en";
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={option.name}
                        avatar={
                          <img
                            src={option.flag}
                            alt={option.name}
                            width={30}
                            height={30}
                            style={{
                              border: `1px solid ${gray[300]}`,
                              borderRadius: "999px",
                              objectFit: "cover",
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                        onDelete={isEnglish ? undefined : rest.onDelete}
                      />
                    );
                  })
                }
                slotProps={{
                  popupIndicator: {
                    sx: { display: "none" },
                  },
                }}
              />
            </Box>
          </Stack>

          {buttonLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Button
              onClick={addNewPortal}
              fullWidth
              sx={{
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                  fontWeight: 700,
                },
              }}
            >
              {addPortalTranslations?.btnAddNewPortal}
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};
